import {
  Smartphone,
  Mail,
  LineChart,
  Handshake,
  Sparkles,
  LucideIcon,
} from "lucide-react";

interface Service {
  id: string;
  Icon: LucideIcon;
  eyebrow: string;
  title: string;
  subtitle?: string;
  intro?: string;
  bullets: string[];
  gain: string[];
  elite?: boolean;
}

const PREMIUM_SERVICES: Service[] = [
  {
    id: "social",
    Icon: Smartphone,
    eyebrow: "Done-for-You",
    title: "Social Media",
    subtitle: "Stay visible. Stay consistent. Stay top of mind.",
    intro: "Every month, our team will:",
    bullets: [
      "Create 20 custom social media posts each month — professional branded graphics, short-form Reels, and high-converting captions with clear calls-to-action",
      "Schedule your content across up to 3 social media platforms",
      "Keep your business consistently visible with 5 posts every week",
    ],
    gain: [
      "Build trust, increase your visibility, and position yourself as the go-to expert in your niche — without spending hours creating content.",
      "While we handle your marketing, you focus on building relationships, serving your clients, and growing your recurring income business.",
    ],
  },
  {
    id: "leads",
    Icon: Mail,
    eyebrow: "Done-for-You",
    title: "Lead Generation",
    subtitle:
      "Your dedicated Growth Specialist fills your pipeline with qualified prospects.",
    intro: "Every month, we will:",
    bullets: [
      "Research qualified prospects within your target market",
      "Build highly targeted prospect lists based on your ideal customer profile",
      "Send up to 200–300 personalized cold outreach emails during your first month",
      "From Month 2 onward, send up to 100 new personalized outreach emails every month",
      "Manage up to 100 personalized follow-up emails",
      "Continuously monitor and optimize your outreach to maximize response rates",
      "Qualify interested prospects and deliver warm leads directly to you",
    ],
    gain: [
      "Stop spending hours searching for prospects or wondering where your next lead will come from.",
      "You'll have a consistent pipeline of qualified opportunities — so you can focus on meaningful conversations and converting more subscribers.",
    ],
  },
  {
    id: "review",
    Icon: LineChart,
    eyebrow: "Every Month",
    title: "Monthly Growth Review",
    subtitle:
      "Clear insights and recommendations so your marketing keeps improving.",
    intro: "Every month you'll receive a business growth review including:",
    bullets: [
      "Social media performance",
      "Lead generation performance",
      "Outreach campaign activity",
      "Qualified lead summary",
      "Strategic recommendations for continued growth",
    ],
    gain: [
      "Know exactly what's working, what can be improved, and where to focus next.",
      "You'll get actionable recommendations every month to help your marketing become more effective — and your business continue to grow.",
    ],
  },
];

const ELITE_SERVICE: Service = {
  id: "elite",
  Icon: Handshake,
  eyebrow: "ELITE only",
  title: "Dedicated WinCampaign Growth Advisor™",
  subtitle:
    "Your dedicated advisor helping you convert qualified prospects into new subscribers.",
  intro: "How ELITE helps you grow faster:",
  bullets: [
    "Up to 10 professionally conducted Discovery Calls every month",
    "Dedicated WinCampaign Growth Advisor™",
    "Growth Advisor introduces the WinCampaign opportunity",
    "Explains the program and answers questions on the call",
    "Helps prospects understand which WinCampaign solution fits them best",
    "One advisor who becomes an extension of your business",
  ],
  gain: [
    "Instead of spending your time conducting Discovery Calls, your Growth Advisor professionally introduces the WinCampaign opportunity, explains the program, answers questions during the call, and helps qualified prospects understand which WinCampaign solution best fits their business.",
    "You get more time to lead your business, serve your clients, and focus on high-value activities — while we help introduce your WinCampaign opportunity to qualified prospects.",
  ],
  elite: true,
};

function ServiceBlock({ service }: { service: Service }) {
  const isElite = !!service.elite;
  const Icon = service.Icon;

  return (
    <div
      data-testid={`wc-service-${service.id}`}
      className={`rounded-3xl overflow-hidden border transition-all duration-500 ${
        isElite
          ? "bg-[#30265b] text-white border-[#673dfc]/40 shadow-2xl shadow-[#673dfc]/20"
          : "bg-white text-[#1a1a24] border-gray-100 hover:shadow-lg hover:shadow-[#673dfc]/5"
      }`}
    >
      <div className="p-8 sm:p-10 lg:p-12">
        <div className="flex flex-col sm:flex-row sm:items-start gap-6 sm:gap-8">
          <div
            className={`w-14 h-14 rounded-2xl grid place-items-center flex-shrink-0 ${
              isElite
                ? "bg-[#d3fc3a] text-[#1a1a24]"
                : "bg-[#f3efff] text-[#673dfc]"
            }`}
          >
            <Icon size={24} strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <div
              className={`text-xs font-nav font-semibold uppercase tracking-[0.14em] ${
                isElite ? "text-[#d3fc3a]" : "text-[#673dfc]"
              }`}
            >
              {service.eyebrow}
            </div>
            <h3
              className={`mt-1 font-display font-bold text-2xl sm:text-3xl leading-[1.1] ${
                isElite ? "text-white" : "text-[#30265b]"
              }`}
            >
              {service.title}
            </h3>
            {service.subtitle && (
              <p
                className={`mt-3 font-display font-medium text-base sm:text-lg leading-relaxed ${
                  isElite ? "text-[#c5b4ff]" : "text-[#673dfc]"
                }`}
              >
                {service.subtitle}
              </p>
            )}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Deliverables */}
          <div className="lg:col-span-3">
            {service.intro && (
              <div
                className={`text-sm font-display font-semibold mb-4 ${
                  isElite ? "text-white/90" : "text-[#30265b]"
                }`}
              >
                {service.intro}
              </div>
            )}
            <ul className="space-y-3">
              {service.bullets.map((b) => (
                <li
                  key={b}
                  className={`flex items-start gap-3 text-sm sm:text-[15px] leading-relaxed ${
                    isElite ? "text-white/85" : "text-gray-700"
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full mt-2.5 flex-shrink-0 ${
                      isElite ? "bg-[#d3fc3a]" : "bg-[#673dfc]"
                    }`}
                  />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* What You Gain — highlighted callout */}
          <div className="lg:col-span-2">
            <div
              className={`relative rounded-2xl p-6 sm:p-7 h-full ${
                isElite
                  ? "bg-[#d3fc3a] text-[#1a1a24]"
                  : "bg-[#f8f6ff] border border-[#c5b4ff]/50"
              }`}
            >
              <div
                className={`inline-flex items-center gap-1.5 text-[10px] font-nav font-semibold uppercase tracking-[0.14em] mb-3 ${
                  isElite
                    ? "text-[#1a1a24]/70"
                    : "text-[#673dfc]"
                }`}
              >
                <Sparkles size={12} /> What you gain
              </div>
              <div className="space-y-3">
                {service.gain.map((p, i) => (
                  <p
                    key={i}
                    className={`text-sm sm:text-[15px] leading-relaxed font-nav ${
                      isElite
                        ? "text-[#1a1a24]/90"
                        : "text-[#30265b]"
                    } ${i === 0 ? "font-display font-semibold" : "font-medium"}`}
                  >
                    {p}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ServiceInclusions() {
  return (
    <section
      id="services"
      data-testid="wc-services"
      className="bg-[#f8f6ff] py-24 sm:py-32"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white text-[#673dfc] border border-[#e5dfff] font-nav font-semibold text-xs rounded-full px-3 py-1 mb-4">
            Your recurring growth system
          </div>
          <h2 className="font-display font-bold text-3xl sm:text-5xl lg:text-[52px] tracking-tight text-[#30265b] leading-[1.05]">
            Everything you need to consistently attract more leads, gain more
            subscribers,{" "}
            <span className="text-[#673dfc]">and build recurring income.</span>
          </h2>
          <p className="mt-5 text-lg text-gray-600 leading-relaxed max-w-2xl">
            Your dedicated marketing team works behind the scenes every month
            to help you consistently attract qualified leads, nurture
            prospects, and grow your subscriber base — so you can focus on
            building relationships and increasing your recurring income.
          </p>
        </div>

        <div className="mt-14 sm:mt-16 space-y-6 lg:space-y-8">
          {PREMIUM_SERVICES.map((s) => (
            <ServiceBlock key={s.id} service={s} />
          ))}
        </div>

        {/* ELITE upgrade block */}
        <div className="mt-16 sm:mt-20">
          <div className="max-w-3xl">
            <h3 className="font-display font-bold text-2xl sm:text-4xl text-[#30265b] leading-[1.1]">
              Stop selling alone.{" "}
              <span className="text-[#673dfc]">
                Upgrade to ELITE and let a dedicated Growth Advisor help you
                close more subscribers.
              </span>
            </h3>
            <div className="mt-5 space-y-3 text-lg text-gray-700 leading-relaxed">
              <p className="font-display font-semibold text-[#30265b]">
                You&apos;ve already built the visibility. Now it&apos;s time
                to increase conversions.
              </p>
              <p>
                Your dedicated WinCampaign Growth Advisor becomes an extension
                of your business — professionally introducing the WinCampaign
                opportunity, explaining the program, answering questions on
                the call, and helping qualified prospects understand which
                WinCampaign solution best fits their business.
              </p>
            </div>
          </div>
          <div className="mt-10">
            <ServiceBlock service={ELITE_SERVICE} />
          </div>
        </div>
      </div>
    </section>
  );
}
